import { useRef } from "react";
import type { Fill, GradientStop } from "../types";
import {
  cloneFill,
  fillToCss,
  GRADIENT_PRESETS,
  solidFill,
  SOLID_PRESETS,
} from "../utils/fill";

interface Props {
  value: Fill;
  onChange: (next: Fill) => void;
  /** Optional section heading inside the editor. */
  label?: string;
  /**
   * Fractional index of the stop that is currently "selected" for
   * editing. This editor manages that selection internally.
   */
}

/**
 * Photoshop-style fill editor supporting:
 *   - Solid or linear gradient modes (tab switch).
 *   - Preset swatches / gradients above the picker.
 *   - Hex + native color picker for solids.
 *   - Draggable stop markers for gradients, with per-stop color +
 *     position + delete, and click-on-empty-track to add a stop.
 */
export function FillEditor({ value, onChange, label }: Props) {
  const isGradient = value.kind === "gradient";

  function toSolid() {
    if (value.kind === "solid") return;
    // Keep the first stop's color as the solid target so switching
    // feels like "flatten to first colour" rather than resetting.
    const first = value.stops[0]?.color ?? "#ffffff";
    onChange(solidFill(first));
  }

  function toGradient() {
    if (value.kind === "gradient") return;
    // Promote the current solid to a 2-stop gradient (same color →
    // white) so the user has something to edit.
    const c = value.color || "#ffffff";
    onChange({
      kind: "gradient",
      angle: 90,
      stops: [
        { pos: 0, color: c },
        { pos: 1, color: "#ffffff" },
      ],
    });
  }

  return (
    <div className="fill-editor">
      {label && <div className="fill-editor-label">{label}</div>}

      <div className="seg" style={{ marginBottom: 8 }}>
        <button
          type="button"
          className={`seg-btn ${!isGradient ? "active" : ""}`}
          onClick={toSolid}
        >
          Solid
        </button>
        <button
          type="button"
          className={`seg-btn ${isGradient ? "active" : ""}`}
          onClick={toGradient}
        >
          Gradient
        </button>
      </div>

      {!isGradient && (
        <SolidEditor
          value={value}
          onChange={(f) => onChange(f)}
        />
      )}
      {isGradient && (
        <GradientEditor
          value={value}
          onChange={(f) => onChange(f)}
        />
      )}
    </div>
  );
}

// -----------------------------------------------------------------
// Solid mode
// -----------------------------------------------------------------

function SolidEditor({
  value,
  onChange,
}: {
  value: Extract<Fill, { kind: "solid" }>;
  onChange: (f: Fill) => void;
}) {
  return (
    <div className="stack" style={{ gap: 8 }}>
      <PresetSwatches
        fills={SOLID_PRESETS.map((c) => ({ label: c, fill: solidFill(c) }))}
        isActive={(f) =>
          f.kind === "solid" &&
          f.color.toLowerCase() === value.color.toLowerCase()
        }
        onPick={(f) => onChange(cloneFill(f))}
      />
      <div className="color-row">
        <input
          type="color"
          value={value.color}
          onChange={(e) => onChange(solidFill(e.target.value))}
          className="color-input"
        />
        <input
          className="input mono"
          value={value.color}
          onChange={(e) => onChange(solidFill(e.target.value))}
        />
      </div>
    </div>
  );
}

// -----------------------------------------------------------------
// Gradient mode
// -----------------------------------------------------------------

function GradientEditor({
  value,
  onChange,
}: {
  value: Extract<Fill, { kind: "gradient" }>;
  onChange: (f: Fill) => void;
}) {
  // The selected stop index is stored on the fill's stop array by
  // convention (not persisted in the model): we pick "active stop" as
  // whichever is clicked last. To keep it out of global state we
  // track it locally by index inside the stops array.
  const activeIdxRef = useRef<number>(0);
  // Ensure the index points to an existing stop.
  const activeIdx = Math.min(
    activeIdxRef.current,
    Math.max(0, value.stops.length - 1),
  );

  // The gradient's Scale/Shift apply a transform on stop positions at
  // render-time. We show markers at the TRANSFORMED position so they
  // visually line up with the rendered gradient, and we invert the
  // transform when the user interacts with them so that dragging/
  // clicking always refers to what the user actually sees.
  const spread = value.spread && value.spread > 0 ? value.spread : 1;
  const offset = value.offset ?? 0;
  const fwd = (raw: number) =>
    Math.max(0, Math.min(1, (raw - 0.5) * spread + 0.5 + offset));
  const inv = (vis: number) =>
    spread === 0 ? 0.5 : (vis - 0.5 - offset) / spread + 0.5;

  const setStop = (idx: number, patch: Partial<GradientStop>) => {
    const next = value.stops.map((s, i) =>
      i === idx ? { ...s, ...patch } : s,
    );
    onChange({ ...value, stops: next });
  };

  /** `visualPos` is a 0..1 position in the rendered gradient. */
  const addStopAt = (visualPos: number) => {
    // Find the two adjacent existing stops in visual space so the new
    // stop's color matches the gradient at the click point.
    const sorted = [...value.stops]
      .map((s, i) => ({ ...s, i, vpos: fwd(s.pos) }))
      .sort((a, b) => a.vpos - b.vpos);
    let color = sorted[sorted.length - 1]?.color ?? "#ffffff";
    for (let i = 0; i < sorted.length - 1; i++) {
      if (visualPos >= sorted[i].vpos && visualPos <= sorted[i + 1].vpos) {
        const span = sorted[i + 1].vpos - sorted[i].vpos || 1;
        color = mixHex(
          sorted[i].color,
          sorted[i + 1].color,
          (visualPos - sorted[i].vpos) / span,
        );
        break;
      }
    }
    const rawPos = Math.max(0, Math.min(1, inv(visualPos)));
    const next = [...value.stops, { pos: rawPos, color }];
    onChange({ ...value, stops: next });
    activeIdxRef.current = next.length - 1;
  };

  const removeStop = (idx: number) => {
    if (value.stops.length <= 2) return; // always keep at least 2 stops
    const next = value.stops.filter((_, i) => i !== idx);
    onChange({ ...value, stops: next });
    activeIdxRef.current = Math.max(0, Math.min(activeIdx, next.length - 1));
  };

  const gradientCss = fillToCss(value);

  // ---- Track / stop pointer handlers ----
  const trackRef = useRef<HTMLDivElement | null>(null);
  const dragRef = useRef<{ stopIdx: number; pointerId: number } | null>(null);

  const posFromClient = (clientX: number): number => {
    const el = trackRef.current;
    if (!el) return 0;
    const rect = el.getBoundingClientRect();
    const p = (clientX - rect.left) / rect.width;
    return Math.max(0, Math.min(1, p));
  };

  const onStopPointerDown = (
    e: React.PointerEvent<HTMLButtonElement>,
    idx: number,
  ) => {
    e.preventDefault();
    e.stopPropagation();
    activeIdxRef.current = idx;
    dragRef.current = { stopIdx: idx, pointerId: e.pointerId };
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
    // Force a re-render so the active highlight updates.
    onChange({ ...value });
  };

  const onStopPointerMove = (e: React.PointerEvent<HTMLButtonElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;
    // Dragged marker is in visual space → store the inverted raw pos.
    const visualPos = posFromClient(e.clientX);
    const rawPos = Math.max(0, Math.min(1, inv(visualPos)));
    setStop(drag.stopIdx, { pos: rawPos });
  };

  const onStopPointerUp = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (dragRef.current?.pointerId === e.pointerId) {
      dragRef.current = null;
      try {
        e.currentTarget.releasePointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
    }
  };

  const onTrackClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // Add a new stop where the user clicked, unless they clicked on
    // an existing marker (those stopPropagation above).
    const p = posFromClient(e.clientX);
    addStopAt(p);
  };

  return (
    <div className="stack" style={{ gap: 8 }}>
      <PresetSwatches
        fills={GRADIENT_PRESETS.map((p) => ({ label: p.label, fill: p.fill }))}
        isActive={() => false}
        onPick={(f) => {
          onChange(cloneFill(f));
          activeIdxRef.current = 0;
        }}
        tall
      />

      <div className="gradient-track-wrap">
        <div
          className="gradient-track"
          ref={trackRef}
          style={{ background: gradientCss }}
          onClick={onTrackClick}
          title="Click to add a stop"
        />
        <div className="gradient-stops">
          {value.stops.map((s, i) => (
            <button
              key={i}
              type="button"
              className={`gradient-stop ${
                i === activeIdx ? "active" : ""
              }`}
              style={{
                left: `${fwd(s.pos) * 100}%`,
                background: s.color,
              }}
              onPointerDown={(e) => onStopPointerDown(e, i)}
              onPointerMove={onStopPointerMove}
              onPointerUp={onStopPointerUp}
              onPointerCancel={onStopPointerUp}
              title={`Stop ${i + 1}: ${s.color} @ ${Math.round(s.pos * 100)}%`}
            />
          ))}
        </div>
      </div>

      <div className="row">
        <label className="label">Stop</label>
        <div className="color-row" style={{ gridColumn: "2 / span 2" }}>
          <input
            type="color"
            value={value.stops[activeIdx]?.color ?? "#ffffff"}
            onChange={(e) => setStop(activeIdx, { color: e.target.value })}
            className="color-input"
          />
          <input
            className="input mono"
            value={value.stops[activeIdx]?.color ?? ""}
            onChange={(e) => setStop(activeIdx, { color: e.target.value })}
          />
          <button
            type="button"
            className="btn danger"
            onClick={() => removeStop(activeIdx)}
            disabled={value.stops.length <= 2}
            title={
              value.stops.length <= 2
                ? "At least 2 stops required"
                : "Delete stop"
            }
          >
            ✕
          </button>
        </div>
      </div>

      <div className="row">
        <label className="label">Position</label>
        <input
          type="range"
          min={0}
          max={1}
          step={0.01}
          value={value.stops[activeIdx]?.pos ?? 0}
          onChange={(e) =>
            setStop(activeIdx, { pos: parseFloat(e.target.value) })
          }
          className="range"
        />
        <input
          type="number"
          min={0}
          max={1}
          step={0.01}
          value={Number(
            (value.stops[activeIdx]?.pos ?? 0).toFixed(3),
          )}
          onChange={(e) => {
            const v = parseFloat(e.target.value);
            if (Number.isFinite(v)) setStop(activeIdx, { pos: v });
          }}
          className="num"
        />
      </div>

      <div className="row">
        <label className="label">Angle</label>
        <input
          type="range"
          min={0}
          max={360}
          step={1}
          value={value.angle}
          onChange={(e) =>
            onChange({ ...value, angle: parseFloat(e.target.value) })
          }
          className="range"
        />
        <input
          type="number"
          min={0}
          max={360}
          step={1}
          value={value.angle}
          onChange={(e) => {
            const v = parseFloat(e.target.value);
            if (Number.isFinite(v)) onChange({ ...value, angle: v });
          }}
          className="num"
        />
      </div>

      {/* Scale (spread) — compresses/stretches all stops around the
          gradient centre. 1 = pass-through, < 1 pinch inwards,
          > 1 push outwards (clipped to 0..1 at the ends). */}
      <div className="row">
        <label className="label">Scale</label>
        <input
          type="range"
          min={0.1}
          max={3}
          step={0.01}
          value={value.spread ?? 1}
          onChange={(e) =>
            onChange({ ...value, spread: parseFloat(e.target.value) })
          }
          className="range"
          title="Compress / stretch all stops around the midpoint"
        />
        <input
          type="number"
          min={0.1}
          max={3}
          step={0.01}
          value={Number((value.spread ?? 1).toFixed(2))}
          onChange={(e) => {
            const v = parseFloat(e.target.value);
            if (Number.isFinite(v)) onChange({ ...value, spread: v });
          }}
          className="num"
        />
      </div>

      {/* Offset — slides all stops along the gradient axis together. */}
      <div className="row">
        <label className="label">Shift</label>
        <input
          type="range"
          min={-1}
          max={1}
          step={0.01}
          value={value.offset ?? 0}
          onChange={(e) =>
            onChange({ ...value, offset: parseFloat(e.target.value) })
          }
          className="range"
          title="Shift all stops together along the gradient axis"
        />
        <input
          type="number"
          min={-1}
          max={1}
          step={0.01}
          value={Number((value.offset ?? 0).toFixed(2))}
          onChange={(e) => {
            const v = parseFloat(e.target.value);
            if (Number.isFinite(v)) onChange({ ...value, offset: v });
          }}
          className="num"
        />
      </div>
    </div>
  );
}

// -----------------------------------------------------------------
// Shared: preset swatch row
// -----------------------------------------------------------------

function PresetSwatches({
  fills,
  isActive,
  onPick,
  tall,
}: {
  fills: { label: string; fill: Fill }[];
  isActive: (f: Fill) => boolean;
  onPick: (f: Fill) => void;
  tall?: boolean;
}) {
  return (
    <div className={`presets ${tall ? "tall" : ""}`}>
      {fills.map((f, i) => (
        <button
          key={i}
          type="button"
          className={`preset ${isActive(f.fill) ? "active" : ""}`}
          style={{ background: fillToCss(f.fill) }}
          onClick={() => onPick(f.fill)}
          title={f.label}
        />
      ))}
    </div>
  );
}

// -----------------------------------------------------------------
// Small colour helpers
// -----------------------------------------------------------------

/** Linearly interpolate two hex colors in sRGB. Input like "#rrggbb". */
function mixHex(a: string, b: string, t: number): string {
  const ca = parseHex(a);
  const cb = parseHex(b);
  const mix = (x: number, y: number) => Math.round(x + (y - x) * t);
  return rgbToHex(mix(ca.r, cb.r), mix(ca.g, cb.g), mix(ca.b, cb.b));
}

function parseHex(hex: string): { r: number; g: number; b: number } {
  const h = hex.replace("#", "").trim();
  const full =
    h.length === 3
      ? h
          .split("")
          .map((c) => c + c)
          .join("")
      : h.padEnd(6, "0");
  return {
    r: parseInt(full.slice(0, 2), 16) || 0,
    g: parseInt(full.slice(2, 4), 16) || 0,
    b: parseInt(full.slice(4, 6), 16) || 0,
  };
}

function rgbToHex(r: number, g: number, b: number): string {
  const clamp = (n: number) => Math.max(0, Math.min(255, Math.round(n)));
  const h = (n: number) => clamp(n).toString(16).padStart(2, "0");
  return `#${h(r)}${h(g)}${h(b)}`;
}
